import { Box, Stack } from '@dtx-company/design-system/src'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import {
  LOCG_DESIGN_SECTIONS,
  SCROLL_CONTAINER_ID,
  TRANSITION_DURATION
} from '../LoggedOutCodeGenerator.constants'
import { LoggedOutGeneratorSection } from '../LoggedOutCodeGenerator.types'
import { ScanDestination } from '../GeneratorSections/ScanDestination/ScanDestination'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useLOCGSectionTransitionStyles } from './hooks/useLOCGSectionTransitionStyles'
import { useScrollableContainerState } from './hooks/useScrollableContainerState'
import { useSelector } from '@xstate/react'
import React from 'react'
import dynamic from 'next/dynamic'

const AuthSection = dynamic(
  () => import('../GeneratorSections/AuthSection/AuthSection').then(mod => mod.AuthSection),
  { ssr: false }
)

export const ScrollableContainer: React.FC = () => {
  const { locgService } = useGlobalServices()
  const { rootContainerRef, scanDestinationRef, codeDesignContainerRef, codeDesignRefs, authRef } =
    useScrollableContainerState()
  const displayScanDestination = useSelector(locgService, state => state.matches('scanDestination'))
  const displayCodeDesign = useSelector(locgService, state => state.matches('codeDesign'))
  const displayAuthForm = useSelector(locgService, state => state.matches('auth'))

  const { scanDestinationSectionStyles, codeDesignSectionStyles, authSectionStyles } =
    useLOCGSectionTransitionStyles(
      rootContainerRef.current ? `${rootContainerRef.current.clientHeight}px` : '100vh'
    )

  return (
    <Stack
      sx={{
        height: '100%',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        pb: 50, // reserve space for intersection observer to trigger on last design section
        // transition styles
        position: 'relative',
        ...scanDestinationSectionStyles,
        ...codeDesignSectionStyles,
        ...authSectionStyles
      }}
      ref={rootContainerRef}
      id={SCROLL_CONTAINER_ID}
    >
      <TransitionGroup>
        {displayScanDestination && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.ScanDestination}
            nodeRef={scanDestinationRef}
          >
            <Box
              id={LoggedOutGeneratorSection.ScanDestination}
              sx={theme => ({
                ml: 8,
                mr: { xs: 8, xl: 0 },
                pt: {
                  md: 10
                },
                pb: 40,
                maxWidth: {
                  md: theme.spacing(136)
                }
              })}
              ref={scanDestinationRef}
            >
              <ScanDestination />
            </Box>
          </CSSTransition>
        )}

        {displayCodeDesign && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.CodeDesign}
            nodeRef={codeDesignContainerRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.CodeDesign}
              ref={codeDesignContainerRef}
              spacing={{
                xs: 8,
                md: 16
              }}
              sx={{
                ml: 8,
                mr: { xs: 8, xl: 0 },
                pt: {
                  md: 10
                },
                pb: {
                  xs: 20,
                  md: 40
                }
              }}
            >
              {LOCG_DESIGN_SECTIONS.map(({ id, Component }, i) => (
                <Box
                  key={id}
                  id={id}
                  ref={el => {
                    codeDesignRefs.current[i] = el as HTMLFieldSetElement
                  }}
                  sx={theme => ({
                    maxWidth: {
                      md: theme.spacing(136)
                    }
                  })}
                >
                  <Component />
                </Box>
              ))}
            </Stack>
          </CSSTransition>
        )}

        {displayAuthForm && (
          <CSSTransition
            timeout={TRANSITION_DURATION}
            classNames={LoggedOutGeneratorSection.SignUp}
            nodeRef={authRef}
          >
            <Stack
              id={LoggedOutGeneratorSection.SignUp}
              ref={authRef}
              gap={2}
              alignItems="flex-start"
              sx={{
                ml: 8,
                mr: { xs: 8, md: 6, xl: 0 },
                pb: {
                  xs: 5,
                  lg: 13
                },
                // left-align button icon to the start of the heading below it
                '& > button:first-of-type': {
                  ml: -3
                }
              }}
            >
              <AuthSection />
            </Stack>
          </CSSTransition>
        )}
      </TransitionGroup>
    </Stack>
  )
}
